import React, { useState, useEffect } from 'react';
import { Loader2, FileText, RefreshCcw, Languages, MessageCircle } from 'lucide-react';
import AppShell from '../../components/AppShell';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_API_BASE;

export default function SmartDoc() {
  const [file, setFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [text, setText] = useState('');
  const [answer, setAnswer] = useState('');
  const [summary, setSummary] = useState('');
  const [translation, setTranslation] = useState('');
  const [history, setHistory] = useState([]);
  const [question, setQuestion] = useState('');
  const [language, setLanguage] = useState('Hindi');
  const [selectedText, setSelectedText] = useState('');
  const [loading, setLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [asking, setAsking] = useState(false);
const [summarizing, setSummarizing] = useState(false);
const [summarizingPart, setSummarizingPart] = useState(false);
const [translating, setTranslating] = useState(false);
const { t } = useTranslation();

// Added spinners and processing
  useEffect(() => {
    fetchWithAuth(`${API}/api/smart-doc/my-files`)
      .then((res) => res?.json())
      .then(setHistory)
      .catch(console.error);
  }, []);

  useEffect(() => {
    document.title = t('Smart Document Assistant') + "– Ennoverse";
  }, []);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetchWithAuth(`${API}/api/smart-doc/upload`, {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();
      setFileId(data._id);
      setText(data.textContent);
    } finally {
      setUploading(false);
    }
  };

  const ask = async () => {
    setAsking(true);
    try {
      const res = await fetchWithAuth(`${API}/api/smart-doc/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId, question }),
      });
      const data = await res.json();
      setAnswer(data.answer);
    } finally {
      setAsking(false);
    }
  };

  const summarize = async () => {
    setSummarizing(true);
    try {
      const res = await fetchWithAuth(`${API}/api/smart-doc/summarize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileId }),
      });
      const data = await res.json();
      setSummary(data.summary);
    } finally {
      setSummarizing(false);
    }
  };

  const summarizePart = async () => {
    setSummarizingPart(true);
    try {
      const res = await fetchWithAuth(`${API}/api/smart-doc/summarize-part`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: selectedText }),
      });
      const data = await res.json();
      setSummary(data.summary);
    } finally {
      setSummarizingPart(false);
    }
  };

  const translate = async () => {
    setTranslating(true);
    try {
      const res = await fetchWithAuth(`${API}/api/smart-doc/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: selectedText || text, language }),
      });
      const data = await res.json();
      setTranslation(data.translation);
    } finally {
      setTranslating(false);
    }
  };

  const exportText = (content, type = 'txt') => {
    const blob = new Blob([content], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ai_response.${type}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <AppShell>
      <div className="lg:col-span-8 space-y-6">
      <div className="text-center space-y-1">
        <h1 className="text-2xl font-semibold flex items-center justify-center gap-2">
        <FileText className="w-5 h-5 text-primary" />
          {t('smart_doc_assistant')}
        </h1>
        <p className="text-gray-500 text-sm">{t('smart_doc_subtext')}</p>
      </div>

        <div className="space-y-4">
          <input type="file" onChange={(e) => setFile(e.target.files[0])} className="border px-3 py-2 rounded w-full" />
          <button onClick={handleUpload} className="bg-primary text-white px-4 py-2 rounded" disabled={uploading}>
  {uploading ? <Loader2 className="animate-spin" size={16} /> : t('upload_process')}
</button>
        </div>

        <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium">{ t('ask_question') }</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                className="border p-2 w-full rounded"
                placeholder="e.g. What is the summary of this document?"
              />
              <button onClick={ask} className="bg-black text-white px-4 py-2 rounded" title="Ask a question" disabled={asking}>
  {asking ? <Loader2 className="animate-spin" size={16} /> : <MessageCircle size={16} />}
</button>
            </div>
            {answer && <div className="bg-gray-100 p-3 rounded text-sm">{answer}</div>}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{ t('translate_to') }</label>
            <div className="flex gap-2">
              <select value={language} onChange={(e) => setLanguage(e.target.value)} className="border p-2 rounded w-full">
                <option>Hindi</option>
                <option>Spanish</option>
                <option>Arabic</option>
                <option>French</option>
              </select>
              <button onClick={translate} className="bg-emerald-600 text-white px-4 py-2 rounded" title="Translate" disabled={translating}>
  {translating ? <Loader2 className="animate-spin" size={16} /> : <Languages size={16} />}
</button>
            </div>
            {translation && <div className="bg-green-50 p-3 rounded text-sm">{translation}</div>}
          </div>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">{t('summarize')}</label>
          <div className="flex gap-2 flex-wrap">
          <button onClick={summarize} className="bg-indigo-600 text-white px-4 py-2 rounded" title="Summarize full doc" disabled={summarizing}>
  {summarizing ? <Loader2 className="animate-spin" size={16} /> : t('summarize_entire')}
</button>
            <button onClick={summarizePart} className="bg-indigo-500 text-white px-4 py-2 rounded" title="Summarize selected text" disabled={summarizingPart}>
  {summarizingPart ? <Loader2 className="animate-spin" size={16} /> : t('summarize_selection')}
</button>
          </div>
          {summary && <div className="bg-yellow-50 p-3 rounded text-sm">{summary}</div>}
        </div>

        {history.length > 0 && (
          <div>
            <h4 className="font-semibold mb-2">📂 {t('recently_uploaded')}</h4>
            <ul className="space-y-1 text-sm">
              {history.map((doc) => (
                <li key={doc._id} className="border-b py-1 flex justify-between">
                  <span>{doc.fileName}</span>
                  <span className="text-gray-400">{new Date(doc.createdAt).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="lg:col-span-4 space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="text-lg font-semibold">{t('file_preview')}</h4>
          <button
            onClick={() => setPreviewVisible(!previewVisible)}
            className="text-sm underline text-blue-600"
          >
            {previewVisible ? t('hide') : t('show')} {t('preview')}
          </button>
        </div>

        {previewVisible && (
          <div
            className="border-2 border-dashed p-4 rounded-lg bg-white shadow-inner max-h-[500px] overflow-y-auto"
            onMouseUp={() => {
              const selection = window.getSelection().toString();
              if (selection.length > 5) setSelectedText(selection);
            }}
          >
            <pre className="whitespace-pre-wrap text-sm text-gray-800">{text}</pre>
          </div>
        )}
      </div>
    </AppShell>
  );
}